import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type {
  BankSummaryView, FinalSettlementView, GpssaReportView, PaymentBatchLineView, PaymentBatchView,
  PayrollRegisterView, ProvisionMovementRow, SettlementConfigView, WpsFieldSourceView,
  WpsLayoutFieldView, WpsLayoutView,
} from '../types'

const WPS = '/v1/wps'
const PAY = '/v1/payments'
const SET = '/v1/settlements'
const REP = '/v1/payroll-reports'

export function useWpsLayouts() {
  return useQuery({
    queryKey: ['wps', 'layouts'],
    queryFn: async () => (await apiClient.get<WpsLayoutView[]>(`${WPS}/layouts`)).data,
  })
}

export function useWpsLayoutFields(layoutId: string | null) {
  return useQuery({
    queryKey: ['wps', 'layouts', layoutId, 'fields'],
    enabled: Boolean(layoutId),
    queryFn: async () =>
      (await apiClient.get<WpsLayoutFieldView[]>(`${WPS}/layouts/${layoutId}/fields`)).data,
  })
}

export function useWpsFieldSources() {
  return useQuery({
    queryKey: ['wps', 'field-sources'],
    queryFn: async () =>
      (await apiClient.get<WpsFieldSourceView[]>(`${WPS}/field-sources`)).data,
    staleTime: Infinity,
  })
}

export function useWpsMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['wps'] })
  }

  return {
    createLayout: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<WpsLayoutView>(`${WPS}/layouts`, body)).data,
      onSuccess: invalidate,
    }),
    addField: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<WpsLayoutFieldView>(`${WPS}/layouts/${id}/fields`, body)).data,
      onSuccess: invalidate,
    }),
    verify: useMutation({
      mutationFn: async ({ id, note }: { id: string; note: string }) =>
        (await apiClient.post<WpsLayoutView>(`${WPS}/layouts/${id}/verify`, { note })).data,
      onSuccess: invalidate,
    }),
  }
}

export function usePaymentBatches() {
  return useQuery({
    queryKey: ['payments', 'batches'],
    queryFn: async () => (await apiClient.get<PaymentBatchView[]>(`${PAY}/batches`)).data,
  })
}

export function usePaymentBatchLines(batchId: string | null) {
  return useQuery({
    queryKey: ['payments', 'batches', batchId, 'lines'],
    enabled: Boolean(batchId),
    queryFn: async () =>
      (await apiClient.get<PaymentBatchLineView[]>(`${PAY}/batches/${batchId}/lines`)).data,
  })
}

export function usePaymentMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['payments'] })
  }

  return {
    prepare: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<PaymentBatchView>(`${PAY}/batches`, body)).data,
      onSuccess: invalidate,
    }),
    generate: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<PaymentBatchView>(`${PAY}/batches/${id}/generate`)).data,
      onSuccess: invalidate,
    }),
    markSent: useMutation({
      mutationFn: async ({ id, bankReference }: { id: string; bankReference?: string }) =>
        (await apiClient.post<PaymentBatchView>(
          `${PAY}/batches/${id}/sent`, { bankReference })).data,
      onSuccess: invalidate,
    }),
    confirm: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<PaymentBatchView>(`${PAY}/batches/${id}/confirm`)).data,
      onSuccess: invalidate,
    }),
  }
}

export function useSettlements() {
  return useQuery({
    queryKey: ['settlements'],
    queryFn: async () => (await apiClient.get<FinalSettlementView[]>(SET)).data,
  })
}

export function useSettlement(id: string | undefined) {
  return useQuery({
    queryKey: ['settlements', id],
    enabled: Boolean(id),
    queryFn: async () => (await apiClient.get<FinalSettlementView>(`${SET}/${id}`)).data,
  })
}

export function useSettlementConfigs() {
  return useQuery({
    queryKey: ['settlements', 'config'],
    queryFn: async () => (await apiClient.get<SettlementConfigView[]>(`${SET}/config`)).data,
  })
}

export function useSettlementMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['settlements'] })
  }

  return {
    raise: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<FinalSettlementView>(SET, body)).data,
      onSuccess: invalidate,
    }),
    calculate: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<FinalSettlementView>(`${SET}/${id}/calculate`)).data,
      onSuccess: invalidate,
    }),
    approve: useMutation({
      mutationFn: async ({ id, remarks }: { id: string; remarks?: string }) =>
        (await apiClient.post<FinalSettlementView>(`${SET}/${id}/approve`, { remarks })).data,
      onSuccess: invalidate,
    }),
    cancel: useMutation({
      mutationFn: async ({ id, remarks }: { id: string; remarks?: string }) =>
        (await apiClient.post<FinalSettlementView>(`${SET}/${id}/cancel`, { remarks })).data,
      onSuccess: invalidate,
    }),
    statement: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<FinalSettlementView>(`${SET}/${id}/statement`)).data,
      onSuccess: invalidate,
    }),
    saveConfig: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<SettlementConfigView>(`${SET}/config`, body)).data,
      onSuccess: invalidate,
    }),
  }
}

export function usePayrollRegister(runId: string | null) {
  return useQuery({
    queryKey: ['payroll-reports', 'register', runId],
    enabled: Boolean(runId),
    queryFn: async () =>
      (await apiClient.get<PayrollRegisterView>(`${REP}/register/${runId}`)).data,
  })
}

export function useBankSummary(batchId: string | null) {
  return useQuery({
    queryKey: ['payroll-reports', 'bank-summary', batchId],
    enabled: Boolean(batchId),
    queryFn: async () =>
      (await apiClient.get<BankSummaryView>(`${REP}/bank-summary/${batchId}`)).data,
  })
}

export function useGpssaReport(year: number, month: number) {
  return useQuery({
    queryKey: ['payroll-reports', 'gpssa', year, month],
    queryFn: async () =>
      (await apiClient.get<GpssaReportView>(`${REP}/gpssa`, { params: { year, month } })).data,
    placeholderData: (previous) => previous,
  })
}

export function useProvisionMovement(year: number, month: number) {
  return useQuery({
    queryKey: ['payroll-reports', 'provisions', year, month],
    queryFn: async () =>
      (await apiClient.get<ProvisionMovementRow[]>(
        `${REP}/provisions`, { params: { year, month } })).data,
    placeholderData: (previous) => previous,
  })
}
